/**
 * Single source of truth for "what counts as a fully-onboarded user".
 *
 * Used by:
 *   - api `/me` to attach an `onboarding` field to every response
 *   - web `<PostAuthGate>` to decide whether to redirect to /register
 *   - web `<Register>` step 2 to validate before letting the user verify
 *     their email
 *
 * Adding a new entry to REQUIRED_ONBOARDING_FIELDS will force any
 * already-signed-up user who's missing that field through the wizard
 * on their next sign-in. That's the design intent — when we introduce
 * a new mandatory profile field (e.g. `taxId` for invoicing), we don't
 * have to backfill it; the wizard collects it at next login.
 */
/**
 * Field-level requirement keys. `firm` is special — it means "this user
 * has at least one firm membership" rather than a column on the user row.
 */
export const REQUIRED_ONBOARDING_FIELDS = ['firstName', 'phone', 'firm'];
export function getMissingOnboardingFields(input) {
    const missing = [];
    for (const field of REQUIRED_ONBOARDING_FIELDS) {
        switch (field) {
            case 'firstName':
                if (!input.user.firstName?.trim())
                    missing.push('firstName');
                break;
            case 'phone':
                if (!input.user.phone?.trim())
                    missing.push('phone');
                break;
            case 'firm':
                if (!input.hasFirm)
                    missing.push('firm');
                break;
        }
    }
    return missing;
}
export function checkOnboarding(input) {
    const missing = getMissingOnboardingFields(input);
    return { complete: missing.length === 0, missing };
}
/* ------------------------------------------------------------------ */
/* Wizard step-2 pre-verify check                                     */
/*                                                                    */
/* The user fills the form BEFORE verifying email. This check tells   */
/* the wizard which form fields are missing so it can surface inline  */
/* errors when the user clicks "Continue with Google" or "send code". */
/* The fields differ from the post-onboarding check because firmName  */
/* is a typed string here (the firm row doesn't exist yet).           */
/* ------------------------------------------------------------------ */
export const REQUIRED_PRE_VERIFY_FIELDS = ['firstName', 'firmName', 'phone'];
export function getMissingPreVerifyFields(input) {
    const missing = [];
    if (!input.firstName.trim())
        missing.push('firstName');
    if (!input.firmName.trim())
        missing.push('firmName');
    if (!input.phone.trim())
        missing.push('phone');
    return missing;
}
//# sourceMappingURL=onboarding.js.map